import { useEffect, useMemo, useRef } from "react";
import ReactPixel from "react-facebook-pixel";
import { connect } from "react-redux";

// Track initialized pixels to avoid re-initialization
const initializedPixels = new Set<string>();

const Component = ({ group, post, subscription, groupStates, currentPage }) => {
  const previousPixelIdRef = useRef<string>("");

  const pixelId: string = useMemo(() => {
    const resolvedPixelId =
      group?.expert?.account_fb_pixel_id ||
      subscription?.expert?.account_fb_pixel_id ||
      post?.group?.expert?.account_fb_pixel_id ||
      "";

    console.log("[Analytics] Resolved pixelId", {
      groupPixel: group?.expert?.account_fb_pixel_id,
      subscriptionPixel: subscription?.expert?.account_fb_pixel_id,
      postPixel: post?.group?.expert?.account_fb_pixel_id,
      resolvedPixelId,
      currentPage,
    });

    return resolvedPixelId;
  }, [group, subscription, post, currentPage]);

  const advancedMatching = undefined;
  const options = {
    autoConfig: true,
    debug: process.env.REACT_APP_ENVIRONMENT !== 'production', // Enable debug in non-production
  };

  const init = (id: string) => {
    // Validate pixel ID format (should not be empty)
    if (!id || !id.trim()) {
      console.error("[Analytics] Invalid pixel ID - empty or whitespace", { id });
      return false;
    }

    const trimmedId = id.trim();

    // Check if this pixel was already initialized
    if (initializedPixels.has(trimmedId)) {
      console.log("[Analytics] Pixel already initialized, firing pageView only", { pixelId: trimmedId });
      try {
        ReactPixel.pageView();
        return true;
      } catch (error) {
        console.error("[Analytics] Error firing pageView", error);
        return false;
      }
    }

    try {
      console.log("[Analytics] Initializing Pixel", { pixelId: trimmedId });
      ReactPixel.init(trimmedId, advancedMatching, options);
      initializedPixels.add(trimmedId);
      ReactPixel.pageView();
      console.log("[Analytics] Pixel initialized and PageView fired", { pixelId: trimmedId });
      return true;
    } catch (error) {
      console.error("[Analytics] Error initializing pixel", { error, pixelId: trimmedId });
      return false;
    }
  };

  useEffect(() => {
    // Only proceed if we have a valid pixel ID
    if (!pixelId || !pixelId.trim()) {
      console.log("[Analytics] No pixel ID available", {
        pixelId,
        groupId: group?.id,
        postId: post?.id,
        subscriptionId: subscription?.id,
      });
      return;
    }

    // Only initialize if pixel ID changed
    if (previousPixelIdRef.current === pixelId) {
      console.log("[Analytics] Pixel ID unchanged, skipping initialization", { pixelId });
      return;
    }

    console.log("[Analytics] Checking pixel availability", {
      pixelId,
      groupId: group?.id,
      postId: post?.id,
      subscriptionId: subscription?.id,
      previousPixelId: previousPixelIdRef.current,
    });

    const success = init(pixelId);
    
    if (success) {
      previousPixelIdRef.current = pixelId;
    } else {
      console.error("[Analytics] Failed to initialize pixel", { pixelId });
    }
  }, [pixelId, group?.id, post?.id, subscription?.id]);

  return <></>;
};

const state = ({ groupStore, postStore, pageStore, subscriptionStore }) => {
  const { name: currentPage } = pageStore;
  const { data: group, states: groupStates } = groupStore.group;
  const { data: post, states: postStates } = postStore.post;
  const { data: subscription, states: subscriptionStates } =
    subscriptionStore.userSubscription;


  return {
    group,
    groupStates,
    post,
    postStates,
    currentPage,
    subscription,
    subscriptionStates,
  };
};

export default connect(state)(Component);
